import React, { useEffect, useState, useContext } from 'react';
import { useTable, usePagination, useGlobalFilter, useSortBy } from 'react-table';
import 'bootstrap/dist/css/bootstrap.min.css';
import './TableLayout.css';
import { ThemeContext } from "../../Shared/Themes/ThemeContext"; 
import { FontSizeContext } from '../../Shared/Font/FontContext';

export default function DataTable({ 
  columns, 
  data, 
  initialSearchValue, 
  pageIndex: controlledPageIndex, 
  setPageIndex,
  pageSize: controlledPageSize,
  setPageSize: setControlledPageSize
}) {
  const getInitialPageSize = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const pageSizeParam = urlParams.get('pageSize');
    return pageSizeParam ? parseInt(pageSizeParam) : 20;
  };

  const [pageSizeState, setPageSizeState] = React.useState(getInitialPageSize);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    nextPage,
    previousPage,
    setPageSize,
    gotoPage,
    state: { pageIndex, pageSize: currentPageSize, globalFilter },
  } = useTable(
    {
      columns,
      data,
      // KEY FIX: use manualPagination=false but control via gotoPage
      initialState: { 
        pageIndex: controlledPageIndex || 0, 
        pageSize: pageSizeState,
        globalFilter: initialSearchValue 
      },
      // This tells react-table to accept external pageIndex changes
      autoResetPage: false,
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  // KEY FIX: Sync external controlledPageIndex → internal gotoPage
  React.useEffect(() => {
    if (controlledPageIndex !== undefined && controlledPageIndex !== pageIndex) {
      gotoPage(controlledPageIndex);
    }
  }, [controlledPageIndex]);

  // Sync internal pageIndex → external setPageIndex
  React.useEffect(() => {
    if (setPageIndex && pageIndex !== controlledPageIndex) {
      setPageIndex(pageIndex);
    }
  }, [pageIndex]);

  const { themeColor } = useContext(ThemeContext);
  const { fontSize, setFontSize } = useContext(FontSizeContext);

  const handlePageChange = (newPageIndex) => {
    // KEY FIX: Update both internal table state AND external parent state
    gotoPage(newPageIndex);
    
    if (setPageIndex) {
      setPageIndex(newPageIndex);
    }

    const url = new URL(window.location);
    url.searchParams.set('page', newPageIndex + 1);
    if (pageSizeState !== 20) {
      url.searchParams.set('pageSize', pageSizeState);
    }
    window.history.pushState({}, '', url);
  };

  const handlePageSizeChange = (e) => {
    const newSize = Number(e.target.value);
    setPageSizeState(newSize);
    setPageSize(newSize);
    gotoPage(0);

    if (setPageIndex) setPageIndex(0);

    const url = new URL(window.location);
    url.searchParams.set('page', '1');
    if (newSize !== 20) {
      url.searchParams.set('pageSize', newSize);
    } else {
      url.searchParams.delete('pageSize');
    }
    window.history.pushState({}, '', url);
  };

  return (
    <div className="dataTable_wrapper container-fluid">
      {/* Page Size Selector */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <div>
          <div className="row align-items-center">
            <div className="col-auto d-flex align-items-center gap-2">
              <select
                className="form-select form-select-sm"
                value={pageSizeState}
                onChange={handlePageSizeChange}
              >
                {[20, 50, 100].map((size) => (
                  <option key={size} value={size}>
                    Show {size}
                  </option>
                ))}
              </select>

              <select
                className="form-select form-select-sm"
                value={fontSize}
                onChange={(e) => setFontSize(e.target.value)}
              >
                <option value="12px">Small</option>
                <option value="14px">Medium</option>
                <option value="16px">Default</option>
                <option value="18px">Large</option>
                <option value="20px">Extra Large</option>
              </select>
            </div>
          </div>
          <span className="fw-bold">Total Records: {data.length}</span>
        </div>
      </div>

      {/* Table */}
      <div className="table-responsive">
        <table {...getTableProps()} className="table table-striped">
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()} className="dataTable_headerRow">
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    className="dataTable_headerCell"
                    style={{
                      backgroundColor: themeColor,
                      color: 'white',
                      border: '2px solid',
                      borderImage: 'linear-gradient(to right, #ff9966, #ff5e62) 1',
                      textAlign: 'center',
                      fontSize: fontSize,
                    }}
                  >
                    {column.render('Header')}
                    <span>
                      {column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()} className="dataTable_body">
            {page.map((row) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()} className="dataTable_row">
                  {row.cells.map((cell) => {
                    const cellProps = cell.getCellProps();
                    
                    if (cell.column.id === 'Actions' && cell.render('Cell').props) {
                      const originalRender = cell.render('Cell');
                      const enhancedChild = React.cloneElement(originalRender, {
                        currentPage: pageIndex
                      });
                      return (
                        <td
                          {...cellProps}
                          className="dataTable_cell"
                          style={{
                            borderTop: `2px solid ${themeColor}`,
                            borderBottom: `2px solid ${themeColor}`,
                            borderLeft: `2px solid ${themeColor}`,
                            borderRight: `2px solid ${themeColor}`,
                            borderImage: `linear-gradient(to right, ${themeColor}, #ff5e62) 1`,
                            fontSize: fontSize,
                          }}
                        >
                          {enhancedChild}
                        </td>
                      );
                    }
                    
                    return (
                      <td
                        {...cellProps}
                        className="dataTable_cell"
                        style={{
                          borderTop: `2px solid ${themeColor}`,
                          borderBottom: `2px solid ${themeColor}`,
                          borderLeft: `2px solid ${themeColor}`,
                          borderRight: `2px solid ${themeColor}`,
                          borderImage: `linear-gradient(to right, ${themeColor}, #ff5e62) 1`,
                          fontSize: fontSize,
                        }}
                      >
                        {cell.render('Cell')}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      <div className="d-flex align-items-center justify-content-between mt-3">
        <div className="dataTable_pageInfo">
          Page{' '}
          <strong>
            {pageIndex + 1} of {pageOptions.length}
          </strong>
        </div>
        <div className="pagebuttons">
          <button
            className="btn btn-primary me-2 btn1"
            onClick={() => handlePageChange(pageIndex - 1)}
            disabled={!canPreviousPage}
          >
            Prev
          </button>
          <button
            className="btn btn-primary btn1"
            onClick={() => handlePageChange(pageIndex + 1)}
            disabled={!canNextPage}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}